/**
 * Bar nights — the deals a bar programmes into its week: Champions League
 * Tuesday, bucket Friday, quiz night, a band on Saturday. A night says WHEN
 * it is on (weekdays and a time window, a date range, or specific dates),
 * WHAT it covers (categories, items, portions — "Nile Special crates") and
 * the DEAL (percent off, an amount off, or a fixed price).
 *
 * The till prices a line by the nights on at the moment it is added — as a
 * per-line discount, so the cart, the receipt and the reports already know
 * how to show it — and the sale stamps which night it was, for revenue by
 * night. The website does the same when a night is marked online.
 *
 * Plain TypeScript — imported by the React app and Convex. Clock helpers
 * (East Africa Time day ints) come from accommodation.ts.
 */

import { addDays, nowLocalMinutes, todayDayInt, weekday } from "./accommodation";

export type NightDealKind = "percent" | "amount" | "fixed_price";

export interface NightDeal {
  kind: NightDealKind;
  /** Percent off (1–100), UGX off each, or the UGX price each — by kind. */
  value: number;
}

export interface NightSchedule {
  /** 0 = Sunday … 6 = Saturday. Absent or empty = every day. */
  weekdays?: number[];
  /** Local minutes since midnight. */
  startMinutes: number;
  /** ≤ startMinutes = the night runs past midnight. */
  endMinutes: number;
  /** YYYYMMDD, inclusive. */
  fromDay?: number;
  toDay?: number;
  /** Specific days only — a one-off match night. Overrides weekdays/range. */
  dates?: number[];
}

export interface NightCovers {
  categoryIds?: string[];
  productIds?: string[];
  /** Portion names — "Crate of 24" — when only some sizes are in the deal. */
  portions?: string[];
  /** Everything of one menu group. */
  group?: "food" | "drink";
}

export interface BarNight {
  _id: string;
  brandId: string;
  storeId?: string;
  name: string;
  tagline?: string;
  active: boolean;
  /** The website prices with it too (else the bar only). */
  online: boolean;
  schedule: NightSchedule;
  covers: NightCovers;
  deal: NightDeal;
}

export interface NightLine {
  productId: string;
  categoryId?: string | null;
  group?: string | null;
  /** The portion sold ("Crate of 24"). */
  size?: string | null;
}

export interface NightMoment {
  dayInt: number;
  minutes: number;
}

export const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const WEEKDAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export const NIGHT_DEAL_KINDS: ReadonlyArray<{ value: NightDealKind; label: string; hint: string }> = [
  { value: "percent", label: "Percent off", hint: "15% off every covered line" },
  { value: "amount", label: "Amount off", hint: "UGX 1,000 off each" },
  { value: "fixed_price", label: "Fixed price", hint: "UGX 5,000 a bottle, whatever the menu says" },
];

/** Now, in the brand's local day and clock. */
export function momentNow(nowMs: number = Date.now()): NightMoment {
  return { dayInt: todayDayInt(nowMs), minutes: nowLocalMinutes(nowMs) };
}

function dayMatches(s: NightSchedule, dayInt: number): boolean {
  if (s.dates && s.dates.length) return s.dates.includes(dayInt);
  if (s.fromDay && dayInt < s.fromDay) return false;
  if (s.toDay && dayInt > s.toDay) return false;
  if (s.weekdays && s.weekdays.length) return s.weekdays.includes(weekday(dayInt));
  return true;
}

/**
 * Is the night on at this moment? A window that runs past midnight belongs
 * to the day it STARTED: "Friday 20:00–02:00" is still Friday's night at
 * 01:00 on Saturday.
 */
export function nightIsOn(n: { active?: boolean; schedule: NightSchedule }, at: NightMoment): boolean {
  if (n.active === false) return false;
  const s = n.schedule;
  const crosses = s.endMinutes <= s.startMinutes;
  if (!crosses) return dayMatches(s, at.dayInt) && at.minutes >= s.startMinutes && at.minutes < s.endMinutes;
  if (at.minutes >= s.startMinutes) return dayMatches(s, at.dayInt);
  if (at.minutes < s.endMinutes) return dayMatches(s, addDays(at.dayInt, -1));
  return false;
}

/** Does the night's deal reach this line? */
export function nightCoversLine(n: { covers: NightCovers }, line: NightLine): boolean {
  const c = n.covers || {};
  const byProduct = !!c.productIds?.length && c.productIds.includes(String(line.productId));
  const byCategory = !!c.categoryIds?.length && !!line.categoryId && c.categoryIds.includes(String(line.categoryId));
  const byGroup = !!c.group && line.group === c.group;
  const scoped = !!(c.productIds?.length || c.categoryIds?.length || c.group);
  if (scoped && !(byProduct || byCategory || byGroup)) return false;
  if (c.portions?.length) return !!line.size && c.portions.includes(line.size);
  return true;
}

/** The unit price after the deal — never below zero, never above the menu price. */
export function dealUnitPrice(deal: NightDeal, unitPrice: number): number {
  const v = Math.max(0, Number(deal.value) || 0);
  if (deal.kind === "percent") return Math.max(0, Math.round(unitPrice * (1 - Math.min(100, v) / 100)));
  if (deal.kind === "amount") return Math.max(0, Math.round(unitPrice - v));
  return Math.max(0, Math.min(unitPrice, Math.round(v)));
}

export interface NightHit<T extends BarNight = BarNight> {
  night: T;
  unitPrice: number;
  saving: number;
  /** How the till's per-line discount expresses it. */
  discountType: "percentage" | "fixed";
  discountValue: number;
}

/** The best night for a line at a moment — the one that leaves the lowest price. */
export function bestNightFor<T extends BarNight>(
  nights: ReadonlyArray<T>,
  line: NightLine & { unitPrice: number },
  at: NightMoment,
  opts?: { online?: boolean },
): NightHit<T> | null {
  let best: NightHit<T> | null = null;
  for (const n of nights) {
    if (opts?.online && !n.online) continue;
    if (!nightIsOn(n, at) || !nightCoversLine(n, line)) continue;
    const price = dealUnitPrice(n.deal, line.unitPrice);
    if (price >= line.unitPrice) continue;
    if (!best || price < best.unitPrice) {
      const saving = line.unitPrice - price;
      best =
        n.deal.kind === "percent"
          ? { night: n, unitPrice: price, saving, discountType: "percentage", discountValue: Math.min(100, Math.max(0, Number(n.deal.value) || 0)) }
          : { night: n, unitPrice: price, saving, discountType: "fixed", discountValue: saving };
    }
  }
  return best;
}

export function clockLabel(minutes: number): string {
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export function minutesFromClockInput(value: string, fallback: number): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value || "");
  if (!m) return fallback;
  const h = Math.min(23, Math.max(0, Number(m[1])));
  const mm = Math.min(59, Math.max(0, Number(m[2])));
  return h * 60 + mm;
}

/** "Tue, Fri · 18:00–23:00" / "Every day · 17:00–20:00" / "2 dates · 20:00–02:00 (past midnight)" */
export function nightWhenLabel(s: NightSchedule): string {
  const days = s.dates?.length
    ? `${s.dates.length} date${s.dates.length === 1 ? "" : "s"}`
    : !s.weekdays?.length || s.weekdays.length === 7
      ? "Every day"
      : [...s.weekdays].sort((a, b) => a - b).map((d) => WEEKDAY_SHORT[d] ?? "?").join(", ");
  const range = s.fromDay && s.toDay && !s.dates?.length ? " · dated" : "";
  return `${days}${range} · ${clockLabel(s.startMinutes)}–${clockLabel(s.endMinutes)}${s.endMinutes <= s.startMinutes ? " (past midnight)" : ""}`;
}

export function dealLabel(d: NightDeal): string {
  const v = Math.round(Number(d.value) || 0);
  if (d.kind === "percent") return `${v}% off`;
  if (d.kind === "amount") return `UGX ${v.toLocaleString()} off each`;
  return `UGX ${v.toLocaleString()} each`;
}

/** What the deal covers, in words, given name lookups. */
export function coversLabel(c: NightCovers, names: { categories?: Record<string, string>; products?: Record<string, string> } = {}): string {
  const parts: string[] = [];
  if (c.group) parts.push(c.group === "drink" ? "all drinks" : "all food");
  if (c.categoryIds?.length) parts.push(c.categoryIds.map((id) => names.categories?.[id] || "a category").join(", "));
  if (c.productIds?.length) parts.push(c.productIds.map((id) => names.products?.[id] || "an item").join(", "));
  const what = parts.length ? parts.join(" + ") : "the whole menu";
  return c.portions?.length ? `${what} · ${c.portions.join(" / ")} only` : what;
}
